// src/components/forms/helpers/groomingHelpers.ts

export interface SelectedPet {
  petId: number;
  petName: string;
  petType: string;
  rateType?: 'Single' | 'Sharing' | 'Custom';
  customRate?: number;
  numberOfNights?: number;
  groomSmooth?: number;
  groomLong?: number;
  groomCut?: number;
  useSharingRate?: boolean;
  lineItemDiscountPercentage?: number;
  lineItemDiscount?: number;
  isSelected?: boolean; // Toggle flag: preserves data when pet is deselected
}

/**
 * Calculate total grooming count for a pet
 * @param pet - The selected pet
 * @returns Sum of all grooming types
 */
export const calculateGroomingCount = (pet: SelectedPet): number => {
  const smooth = pet.groomSmooth || 0;
  const long = pet.groomLong || 0;
  const cut = pet.groomCut || 0;
  return smooth + long + cut;
};

/**
 * Build grooming types array from pet grooming numbers
 * Expands counts into array of type strings: e.g., [Smooth, Smooth, Long, Cut]
 * @param pet - The selected pet
 * @returns Array of grooming type strings
 */
export const buildGroomingTypesArray = (pet: SelectedPet): string[] => {
  const grooming: string[] = [];

  const smooth = pet.groomSmooth || 0;
  const long = pet.groomLong || 0;
  const cut = pet.groomCut || 0;

  for (let i = 0; i < smooth; i++) {
    grooming.push('Smooth');
  }

  for (let i = 0; i < long; i++) {
    grooming.push('Long');
  }

  for (let i = 0; i < cut; i++) {
    grooming.push('Cut');
  }

  return grooming;
};

/**
 * Build booking pet DTOs from selected pets
 * Transforms UI state into API-ready format
 * @param selectedPets - List of selected pets with their configurations
 * @returns Array of booking pet objects ready for API submission
 */
export const buildBookingPetsPayload = (
  selectedPets: SelectedPet[]
): Array<{
  petId: number;
  kennelId: null;
  useSharingRate: boolean;
  rateType: string;
  customRate?: number;
  lineItemDiscountPercentage: number;
  lineItemDiscount: number;
  groomingTypes: string[];
  numberOfNights?: number;
}> => {
  // Filter: only include pets with isSelected === true (default true for backwards compatibility)
  return selectedPets
    .filter((pet) => pet.isSelected !== false)
    .map((pet) => ({
      petId: pet.petId,
      kennelId: null,
      useSharingRate: pet.useSharingRate || false,
      rateType: pet.rateType || 'Single',
      customRate: pet.customRate,
      lineItemDiscountPercentage: pet.lineItemDiscountPercentage || 0,
      lineItemDiscount: pet.lineItemDiscount || 0,
      groomingTypes: buildGroomingTypesArray(pet),
      numberOfNights: pet.numberOfNights,
    }));
};
